import { Controller } from '@hotwired/stimulus'

const SIDEBAR_MENU_ENABLED = false
const SIDEBAR_BREAKPOINT = 1024

export default class extends Controller {
  static values = {
    redirectUrl: String,
    widgetId: String,
    lang: { type: String, default: 'fr' },
    scriptUrl: String
  }

  static targets = ['loader', 'error']

  static scriptPromise = null

  static loadScript(url) {
    if (window.LemonwayWidget) return Promise.resolve()

    if (!this.scriptPromise) {
      this.scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = url
        script.async = true
        script.onload = () => resolve()
        script.onerror = () => {
          script.remove()
          this.scriptPromise = null
          reject(new Error(`Unable to load ${url}`))
        }
        document.head.appendChild(script)
      })
    }

    return this.scriptPromise
  }

  async start(onboardingUrl) {
    this.stopped = false
    this.hideError()
    this.showLoader()

    try {
      await this.constructor.loadScript(this.scriptUrlValue)
    } catch (error) {
      if (!this.stopped) this.showError()
      return
    }

    if (this.stopped) return

    this.initWidget(onboardingUrl)
    this.hideLoader()
  }

  stop() {
    this.stopped = true

    try {
      window.LemonwayWidget?.destroy(this.widgetIdValue)
    } catch (error) {
      // The widget may not have been initialized yet.
    }
  }

  disconnect() {
    this.stop()
  }

  initWidget(onboardingUrl) {
    LemonwayWidget.init(
      onboardingUrl,
      this.widgetIdValue,
      {
        lang: this.langValue,
        onMyAccount: () => this.redirectToNextStep(false),
        onFinish: () => this.redirectToNextStep(true),
        onPageChange: () => this.scrollToTop(),
        ...this.sidebarOptions()
      }
    )
  }

  sidebarOptions() {
    if (!SIDEBAR_MENU_ENABLED) return {}

    return {
      isSidebarMenuEnabled: true,
      sidebarBreakpoint: SIDEBAR_BREAKPOINT
    }
  }

  scrollToTop() {
    const scrollArea = this.element.closest('[data-lemonway-scroll]') || window

    scrollArea.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  showLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove('hidden')
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
  }

  hideLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.add('hidden')
  }

  showError() {
    this.hideLoader()
    if (this.hasErrorTarget) this.errorTarget.classList.remove('hidden')
  }

  redirectToNextStep(onboardingSubmitted = false) {
    const url = new URL(this.redirectUrlValue)
    if (onboardingSubmitted) {
      url.searchParams.set('onboarding_submitted', 'true')
    }

    window.location.replace(url)
  }
}
